/**
 * PsicoSalud — Administración de cuentas.
 *
 * Es la pantalla propia del perfil administrativo, y responde a la tarea 12
 * del instrumento de evaluación: «identificar qué ve un perfil
 * administrativo». Hasta agosto de 2026 ese perfil no tenía ninguna vista
 * diferenciada, de modo que la pregunta no era contestable: veía lo mismo
 * que un psicólogo, con partes que le respondían error.
 *
 * Lo que gestiona acá —quién tiene acceso al sistema— es exactamente su
 * competencia, y no toca contenido clínico en ningún punto.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api';
import { useAuth } from '@/context/AuthContext';
import type { User } from '@/types';

export default function UsersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await authApi.listUsers());
    } catch {
      setFeedback({ type: 'error', text: t('errors.generic') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const alternarEstado = async (cuenta: User) => {
    setPending(cuenta.id);
    try {
      if (cuenta.is_active) {
        await authApi.deactivateUser(cuenta.id);
        setFeedback({ type: 'success', text: t('users.deactivated') });
      } else {
        await authApi.activateUser(cuenta.id);
        setFeedback({ type: 'success', text: t('users.activated') });
      }
      await load();
    } catch {
      setFeedback({ type: 'error', text: t('errors.generic') });
    } finally {
      setPending(null);
    }
  };

  if (loading) return <div className="empty-state">{t('common.loading')}</div>;

  const activeCount = users.filter((u) => u.is_active).length;
  const adminCount = users.filter((u) => u.role === 'ADMIN').length;
  const psychologistCount = users.filter((u) => u.role === 'PSYCHOLOGIST').length;

  return (
    <>
      <header className="page-header with-flex">
        <div>
          <span className="page-eyebrow">{t('nav.users')}</span>
          <h1>{t('users.title')}</h1>
          <p className="page-descripcion">
            {users.length} cuentas · {adminCount} {t('users.roles.ADMIN').toLowerCase()} ·{' '}
            {psychologistCount} {t('users.roles.PSYCHOLOGIST').toLowerCase()}
          </p>
        </div>
      </header>

      {feedback && (
        <div
          className={`notice notice-${feedback.type}`}
          role="status"
          data-cy={`users-feedback-${feedback.type}`}
        >
          {feedback.text}
        </div>
      )}

      <div className="notice notice-info">{t('users.scopeNotice')}</div>

      <div className="grid grid-3" style={{ marginBottom: 'var(--espacio-5)' }}>
        <div className="card stat" style={{ marginBottom: 0, padding: 'var(--espacio-4)' }}>
          <div className="stat-value t-numerico">{users.length}</div>
          <div className="stat-label">Cuentas totales</div>
        </div>
        <div className="card stat" style={{ marginBottom: 0, padding: 'var(--espacio-4)' }}>
          <div className="stat-value t-numerico">{activeCount}</div>
          <div className="stat-label">{t('users.active')}</div>
        </div>
        <div className="card stat" style={{ marginBottom: 0, padding: 'var(--espacio-4)' }}>
          <div className="stat-value t-numerico">{users.length - activeCount}</div>
          <div className="stat-label">{t('users.inactive')}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-eyebrow">{t('users.title')}</span>
            <h2>{t('users.title')}</h2>
          </div>
        </div>

        <div className="table-wrapper">
          <table data-cy="users-table">
            <thead>
              <tr>
                <th>{t('users.name')}</th>
                <th>{t('users.email')}</th>
                <th style={{ width: '10rem' }}>{t('users.role')}</th>
                <th style={{ width: '8rem' }}>{t('users.status')}</th>
                <th style={{ width: '10rem', textAlign: 'right' }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((cuenta) => (
                <tr key={cuenta.id} data-cy="users-row">
                  <td style={{ fontWeight: 'var(--peso-medio)' }}>{cuenta.full_name}</td>
                  <td>
                    <span className="t-mono t-pequeno t-suave">{cuenta.email}</span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        cuenta.role === 'ADMIN' ? 'badge-warning' : 'badge-info'
                      }`}
                    >
                      {cuenta.role ? t(`users.roles.${cuenta.role}`) : '—'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${cuenta.is_active ? 'badge-success' : 'badge-neutral'}`}
                      data-cy="users-status"
                    >
                      <span className="badge-dot" />
                      {cuenta.is_active ? t('users.active') : t('users.inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="button-row is-final">
                      {/* Un administrador no puede darse de baja a sí mismo: el
                          último quedaría fuera y nadie podría revertirlo. El
                          backend lo rechaza; acá directamente no se ofrece. */}
                      {cuenta.id !== user?.id && (
                        <button
                          className={cuenta.is_active ? 'danger is-pequeno' : 'is-pequeno'}
                          disabled={pending === cuenta.id}
                          onClick={() => void alternarEstado(cuenta)}
                          data-cy="users-toggle"
                        >
                          {cuenta.is_active ? t('users.deactivate') : t('users.activate')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="t-pequeno t-suave" style={{ marginTop: 'var(--espacio-4)' }}>
          {t('users.deactivationNotice')}
        </p>
      </div>
    </>
  );
}
